import { QueryNormalizerProvider } from '@normy/react-query';
import { QueryClientProvider } from '@tanstack/react-query';
import { ConfigCatProvider } from 'configcat-react';
import { lazy, Suspense, useEffect } from 'react';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { config } from '@/config';
import { CurrentUserContextProvider } from '@/contexts';
import { useAnalytics } from '@/hooks/useAnalytics';
import { normalizerConfig } from '@/lib/normalizer-config';
import { queryClient } from '@/lib/query-client';
import { ChakraProvider } from '@/ui';
import { Confetti } from './components/Confetti';
import { AppRouter } from './router';
import { theme } from './theme';
import './i18n';

// Devtools are a development-only aid. Keep them out of the production entry
// bundle entirely (they were previously imported eagerly and even rendered a
// floating button in prod) — `import.meta.env.PROD` is statically replaced, so
// the dynamic import is tree-shaken from production builds.
const ReactQueryDevtools = import.meta.env.PROD
  ? (): null => null
  : lazy(() =>
      import('@tanstack/react-query-devtools').then((module) => ({
        default: module.ReactQueryDevtools,
      }))
    );

export const App = (): JSX.Element => {
  const { init: initAnalytics, trackEvent } = useAnalytics();

  useEffect(() => {
    initAnalytics();
  }, [initAnalytics]);

  useEffect(() => {
    const handler = () => trackEvent('util:focus');
    window.addEventListener('focus', handler);

    return () => window.removeEventListener('focus', handler);
  }, [trackEvent]);

  return (
    <ChakraProvider theme={theme}>
      <ErrorBoundary>
        <QueryNormalizerProvider
          normalizerConfig={normalizerConfig}
          queryClient={queryClient}
        >
          <QueryClientProvider client={queryClient}>
            <Confetti>
              <Suspense fallback={null}>
                <ReactQueryDevtools buttonPosition="bottom-left" />
              </Suspense>
              <ConfigCatProvider sdkKey={config.configCat.key}>
                <CurrentUserContextProvider>
                  <AppRouter />
                </CurrentUserContextProvider>
              </ConfigCatProvider>
            </Confetti>
          </QueryClientProvider>
        </QueryNormalizerProvider>
      </ErrorBoundary>
    </ChakraProvider>
  );
};
